import React from 'react';
import PropTypes from 'prop-types';
import { graphql, compose } from 'react-apollo';

import scanMetricsQuery from './scanMetricsQuery.graphql';

// How well the scan system is actually working.
//
// There has been no measurement of it at all. Competitors publish accuracy
// figures — 95% for Ravin, 95-99% for Inspektlabs — and MotoStream could not
// state its own, could not tell whether a change helped, and had no baseline
// for a vendor comparison.
//
// Placed on the existing admin scan screen rather than in a new dashboard,
// because operations already opens that page and a dashboard nobody visits
// measures nothing.
//
// Styled inline, like the review panel beside it: package.json pins bootstrap
// 3.4.1 while the surrounding screen uses Bootstrap 5 utility classes, so a
// component that depends on those may render unstyled.

const styles = {
  panel: { border: '1px solid #e0e0e0', borderRadius: '6px', padding: '20px', marginBottom: '24px', backgroundColor: '#fbfbfb' },
  heading: { fontWeight: 'bold', fontSize: '18px', marginBottom: '4px' },
  sub: { color: '#666', marginBottom: '16px', fontSize: '14px' },
  row: { display: 'flex', flexWrap: 'wrap', gap: '24px', marginBottom: '20px' },
  metric: { minWidth: '150px' },
  value: { fontSize: '26px', fontWeight: 'bold' },
  label: { color: '#666', fontSize: '13px' },
  denominator: { color: '#999', fontSize: '12px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { textAlign: 'left', borderBottom: '2px solid #e0e0e0', padding: '8px 6px', color: '#666', fontWeight: 'normal' },
  td: { borderBottom: '1px solid #eee', padding: '8px 6px' },
  caveat: { color: '#b8860b', fontSize: '13px', marginTop: '12px' },
  scroll: { overflowX: 'auto' },
};

// A figure, or an honest absence.
//
// Null is not zero. A false-positive rate of 0% reads as "the model is never
// wrong"; the same figure from no reviews means nobody has checked. The API
// returns null for the second, and this must not quietly render it as a number.
function Figure({ value, suffix = '%' }) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return <span style={{ ...styles.value, color: '#999', fontSize: '18px' }}>no data</span>;
  }
  return <span style={styles.value}>{round(value)}{suffix}</span>;
}

function Change({ value }) {
  if (value === null || value === undefined) return <span style={{ color: '#999' }}>—</span>;
  if (value === 0) return <span style={{ color: '#666' }}>no change</span>;
  const up = value > 0;
  return <span style={{ color: '#333' }}>{up ? '+' : ''}{round(value)} pts</span>;
}

// A count, or a dash.
//
// The rates are null-guarded and the denominators beside them were not, so a
// missing `counts` object — which the API falls back to on a partial failure —
// would have rendered "undefined rejected of undefined decided" underneath a
// carefully hedged figure. The whole point of this panel is refusing to state
// what it does not know, and that has to include the small print.
function count(value) {
  return value === null || value === undefined || Number.isNaN(Number(value)) ? '—' : value;
}

// Percentages are rounded here rather than trusted to arrive rounded. The API
// does round them, but a raw float rendered straight into the page would print
// to fifteen decimal places, and this component should not depend on someone
// else's formatting to stay readable.
function round(value) {
  return Math.round(Number(value) * 10) / 10;
}

function pct(value) {
  return value === null || value === undefined || Number.isNaN(Number(value))
    ? 'no data'
    : `${round(value)}%`;
}

class ScanMetricsPanel extends React.Component {
  static propTypes = {
    scanMetrics: PropTypes.object,
  };

  render() {
    const { scanMetrics } = this.props;
    const result = scanMetrics && scanMetrics.getScanMetrics;

    if (!result || scanMetrics.loading) {
      return <div style={styles.panel}>Loading scan metrics…</div>;
    }

    if (result.status !== 200) {
      return (
        <div style={styles.panel}>
          <div style={styles.heading}>Scan accuracy</div>
          <div style={styles.sub}>{result.errorMessage || 'Metrics are unavailable.'}</div>
        </div>
      );
    }

    const { overall, releases } = result;
    const counts = (overall && overall.counts) || {};

    return (
      <div style={styles.panel}>
        <div style={styles.heading}>Scan accuracy</div>
        <div style={styles.sub}>
          Computed from stored data. Nothing here is estimated or collected by hand.
        </div>

        <div style={styles.row}>
          <div style={styles.metric}>
            <Figure value={overall.falsePositiveRate} />
            <div style={styles.label}>False positives</div>
            <div style={styles.denominator}>
              {count(counts.reviewsRejected)} rejected of {count(overall.reviewsDecided)} decided
            </div>
          </div>
          <div style={styles.metric}>
            <Figure value={overall.detectionRate} />
            <div style={styles.label}>Scans finding damage</div>
            <div style={styles.denominator}>{count(counts.scansWithDamage)} of {count(counts.scansAnalysed)} analysed</div>
          </div>
          <div style={styles.metric}>
            <Figure value={overall.reviewRate} />
            <div style={styles.label}>Findings needing review</div>
            <div style={styles.denominator}>{count(counts.findingsQueued)} of {count(counts.findingsTotal)}</div>
          </div>
          <div style={styles.metric}>
            <Figure value={overall.disputeRate} />
            <div style={styles.label}>Trips disputed</div>
            <div style={styles.denominator}>{count(counts.claimsFiled)} of {count(counts.tripsCompleted)} completed</div>
          </div>
          <div style={styles.metric}>
            <Figure value={overall.fraudFlagRate} />
            <div style={styles.label}>Scans with photo flags</div>
            <div style={styles.denominator}>{count(counts.scansFlagged)} of {count(counts.scansScreened)} screened</div>
          </div>
          <div style={styles.metric}>
            <Figure value={overall.reviewsPending} suffix="" />
            <div style={styles.label}>Waiting for review</div>
            <div style={styles.denominator}>findings a person has not judged</div>
          </div>
        </div>

        {releases && releases.length > 0 && (
          <div style={styles.scroll}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Release</th>
                  <th style={styles.th}>Deployed</th>
                  <th style={styles.th}>False positives</th>
                  <th style={styles.th}>Change</th>
                  <th style={styles.th}>Detection</th>
                  <th style={styles.th}>Reviews decided</th>
                </tr>
              </thead>
              <tbody>
                {releases.map((release) => (
                  <tr key={release.releaseId}>
                    <td style={styles.td}>
                      {release.version}
                      {release.branch ? <span style={styles.denominator}> ({release.branch})</span> : null}
                    </td>
                    <td style={styles.td}>
                      {release.deployedAt ? new Date(Number(release.deployedAt) || release.deployedAt).toLocaleDateString() : '—'}
                    </td>
                    <td style={styles.td}>
                      {pct(release.falsePositiveRate)}
                      {/* A figure from too few decisions is shown but marked.
                          "100% accurate" from two reviews gets repeated. */}
                      {!release.reportable && release.falsePositiveRate !== null
                        ? <span style={styles.denominator}> — too few to quote</span>
                        : null}
                    </td>
                    <td style={styles.td}><Change value={release.change && release.change.falsePositiveRate} /></td>
                    <td style={styles.td}>
                      {pct(release.detectionRate)}
                    </td>
                    <td style={styles.td}>{count(release.reviewsDecided)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(!releases || releases.length === 0) && (
          <div style={styles.caveat}>
            No releases recorded yet. Release-over-release comparison begins at the next deploy.
          </div>
        )}
      </div>
    );
  }
}

export default compose(
  graphql(scanMetricsQuery, {
    name: 'scanMetrics',
    options: {
      variables: { releaseLimit: 5 },
      // Metrics move whenever an operator decides something, and this screen is
      // where they decide it.
      fetchPolicy: 'network-only',
    },
  }),
)(ScanMetricsPanel);
